import { ErrorBoundary } from 'react-error-boundary';
import { useContext, useEffect, useRef } from 'react';
import LoggerContext from '../../context/LoggerContext';
import ErrorModal from '../ErrorModal/ErrorModal';
import CallMeModal from '../CallMeModal/CallMeModal';
import axios from 'axios';
import { useHippoText } from '../../hooks/useHippoText';
import { errorTypes } from 'c9-tracking';
import { useErrorTracking } from '../ErrorModal/useErrorTracking';

const ErrorBoundaryComponent = ({ children }: { children: React.ReactNode }): React.JSX.Element => {
    const logger = useContext(LoggerContext);
    const getHippoText = useHippoText();
    const errorBoundaryRef = useRef<ErrorBoundary>(null);
    const { trackFault } = useErrorTracking();
    const activityCode = 'dss_unexpected_error_phone_req';
    const errorRef = useRef<Error | undefined>(undefined);

    useEffect(() => {
        const rejectedPromiseHandler = (event: PromiseRejectionEvent): void => {
            if (!event.reason) return;
            console.log('Unhandled rejection caught');
            if (!errorRef.current) {
                trackFault(
                    `Customer encountered an error modal, errorcode: ${activityCode}`,
                    errorTypes.tech,
                    event.reason
                );
            }
            errorRef.current = event.reason;
            errorBoundaryRef.current?.setState({ didCatch: true, error: event.reason });
        };

        window.addEventListener('unhandledrejection', rejectedPromiseHandler);
        return () => {
            window.removeEventListener('unhandledrejection', rejectedPromiseHandler);
        };
    }, [errorRef, trackFault]);

    const activityCodeMap: Record<string, string> = {
        TOP_PREEMPTION_ERROR: 'dss_pre_emption_phone_req',
        TOP_OUT_OF_SEQUENCE_ERROR: 'dss_oos_phone_req'
    };

    const logError = (error: Error): void => {
        if (!errorRef.current) {
            trackFault(
                `Customer encountered an error modal, errorcode: ${activityCode}`,
                errorTypes.tech,
                error
            );
        }
        errorRef.current = error;
        logger.error(error);
        console.log(error);
    };

    const handleResetError = (): void => {
        console.log('Error boundary reset');
        errorRef.current = undefined;
        errorBoundaryRef.current?.setState({ didCatch: false, error: undefined });
    };

    const getFallbackComponent = (error: Error): React.JSX.Element => {
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.error?.data?.appError;

            if (errorMessage && activityCodeMap[errorMessage]) {
                return (
                    <CallMeModal
                        activityCode={activityCodeMap[errorMessage]}
                        showModal={true}
                        closeModal={() => (window.location.href = '/')}
                        title={getHippoText('DSS-OOS-PREEMPTION-CALL-ME-TITLE')}
                        description={getHippoText('DSS-OOS-PREEMPTION-CALL-ME-DESCRIPTION')}
                        error={error}
                    />
                );
            }
        }

        return <ErrorModal retry={handleResetError} error={error ?? errorRef.current} />;
    };

    return (
        <ErrorBoundary
            ref={errorBoundaryRef}
            onError={logError}
            onReset={handleResetError}
            FallbackComponent={({ error }) => getFallbackComponent(error as Error)}
        >
            {children}
        </ErrorBoundary>
    );
};

export default ErrorBoundaryComponent;
